import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';

export interface SavePlanAsPayload {
    planId: string;
    planName: string;
}

export interface SavePlanAsResponse {
    new_plan_id: string;
    planning_group_id: string;
    status: string;
    error_message: string;
}

const savePlanAs = async ({ planId, planName }: SavePlanAsPayload) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.savePlanAs(planId), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: {
            plan_name: planName,
            created_by: localStorage.getItem('email'),
        },
    });

    if (response?.data?.status !== 'success') {
        throw new Error(response?.data?.error_message || 'Failed to save plan copy');
    }

    return response.data as SavePlanAsResponse;
};

const useSavePlanAsMutation = ({
    onSuccess,
}: {
    onSuccess?: (data: SavePlanAsResponse, payload: SavePlanAsPayload) => void;
} = {}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (payload: SavePlanAsPayload) => savePlanAs(payload),
        onSuccess: (data, payload) => {
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.planningGroups() });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: [...tpoGenKeys.all, 'importable-plans'] });
            sendNotification('success', 'Plan copy saved successfully');
            onSuccess?.(data, payload);
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save plan copy';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useSavePlanAsMutation;
